/**
 * Reddit Pixel Provider
 *
 * Implements Reddit advertising pixel for conversion tracking.
 * Ported from deepsource.com composables/adTracking/providers/reddit.ts
 */

import type { AdTrackingProvider } from '~/types/tracking'

declare global {
  interface Window {
    rdt?: (command: string, ...args: unknown[]) => void
  }
}

const isDev = process.env.NODE_ENV === 'development'

interface RedditPixelConfig {
  pixelId: string
}

export const createRedditProvider = (config: RedditPixelConfig): AdTrackingProvider => {
  let isLoaded = false
  const { pixelId } = config

  const loadScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window not available'))
        return
      }

      if (window.rdt) {
        resolve()
        return
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      window.rdt = function (...args: any[]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rdtFn = window.rdt as any
        if (rdtFn.sendEvent) {
          rdtFn.sendEvent.apply(window.rdt, args)
        } else {
          rdtFn.callQueue.push(args)
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window.rdt as any).callQueue = []

      const script = document.createElement('script')
      script.async = true
      script.src = 'https://www.redditstatic.com/ads/pixel.js'

      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Reddit pixel'))

      document.head.appendChild(script)
    })
  }

  return {
    name: 'reddit',

    async init() {
      if (!pixelId) {
        if (isDev) {
          console.log('Reddit Pixel: disabled (no pixel ID configured)')
        }
        return
      }

      try {
        await loadScript()

        if (window.rdt) {
          window.rdt('init', pixelId)
          isLoaded = true

          if (isDev) {
            console.log('Reddit pixel initialized:', pixelId)
          }
        }
      } catch (error) {
        if (isDev) {
          console.warn('Failed to initialize Reddit pixel:', error)
        }
      }
    },

    trackPageView(url?: string) {
      if (!isLoaded || !window.rdt) return

      window.rdt('track', 'PageVisit', url ? { page_location: url } : undefined)

      if (isDev) {
        console.log('Reddit page view tracked:', url || window.location.href)
      }
    },

    trackEvent(eventName: string, params?: Record<string, unknown>) {
      if (!isLoaded || !window.rdt) return

      window.rdt('track', eventName, params)

      if (isDev) {
        console.log('Reddit event tracked:', eventName, params)
      }
    },

    trackConversion(conversionType: string, params?: Record<string, unknown>) {
      if (!isLoaded || !window.rdt) return

      const eventMap: Record<string, string> = {
        SignUp: 'SignUp',
        CompleteRegistration: 'SignUp',
        Lead: 'Lead',
        Purchase: 'Purchase',
        AddToCart: 'AddToCart',
        InitiateCheckout: 'ViewContent',
        Subscribe: 'SignUp',
        StartTrial: 'Lead'
      }

      const redditEvent = eventMap[conversionType] || 'Custom'

      window.rdt('track', redditEvent, {
        conversionId: conversionType,
        ...params
      })

      if (isDev) {
        console.log('Reddit conversion tracked:', redditEvent, params)
      }
    },

    isAvailable() {
      return Boolean(pixelId && isLoaded)
    },

    cleanup() {
      isLoaded = false
    }
  }
}
